import React from 'react';
import { resolveUrl } from '../../api';

export type DictationState = 'idle' | 'recording' | 'transcribing';

/** Längsta inspelning innan den stoppas av sig själv. */
const MAX_MS = 60_000;

export function dictationSupported(): boolean {
  return typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

function pickMime(): string {
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return '';
}

/**
 * Diktering: spelar in från mikrofonen och skriver ut texten med API:ts /api/transcribe (Whisper),
 * samma tjänst som jaktläget använder. `onText` får den utskrivna texten.
 */
export function useDictation(onText: (text: string) => void, lang: 'sv' | 'en' = 'sv') {
  const [state, setState] = React.useState<DictationState>('idle');
  const [error, setError] = React.useState('');
  const [elapsed, setElapsed] = React.useState(0);
  const rec = React.useRef<MediaRecorder | null>(null);
  const stream = React.useRef<MediaStream | null>(null);
  const cancelled = React.useRef(false);
  const timers = React.useRef<number[]>([]);
  const onTextRef = React.useRef(onText);
  onTextRef.current = onText;

  const cleanup = () => {
    timers.current.forEach((t) => clearInterval(t));
    timers.current = [];
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    rec.current = null;
  };
  React.useEffect(() => () => { cancelled.current = true; rec.current?.state === 'recording' && rec.current.stop(); cleanup(); }, []);

  const start = async () => {
    setError('');
    cancelled.current = false;
    if (!dictationSupported()) { setError('Diktering fungerar inte i den här webbläsaren.'); return; }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      const mime = pickMime();
      const r = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      const chunks: Blob[] = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      r.onstop = async () => {
        const type = r.mimeType || mime || 'audio/webm';
        cleanup();
        if (cancelled.current) { cancelled.current = false; setState('idle'); return; }
        setState('transcribing');
        try {
          const fd = new FormData();
          fd.append('audio', new Blob(chunks, { type }), `diktering${type.includes('mp4') ? '.mp4' : '.webm'}`);
          const res = await fetch(resolveUrl(`/api/transcribe?lang=${lang}`), { method: 'POST', body: fd });
          const json = await res.json().catch(() => ({}));
          const text = typeof json.text === 'string' ? json.text.trim() : '';
          if (text) onTextRef.current(text);
          else setError(json.error ? 'Kunde inte skriva ut ljudet.' : 'Hörde inget – försök igen.');
        } catch {
          setError('Kunde inte skriva ut ljudet.');
        }
        setState('idle');
      };
      rec.current = r;
      r.start();
      setElapsed(0);
      setState('recording');
      const t0 = Date.now();
      timers.current.push(window.setInterval(() => {
        const ms = Date.now() - t0;
        setElapsed(Math.floor(ms / 1000));
        if (ms >= MAX_MS && rec.current?.state === 'recording') rec.current.stop();
      }, 250));
    } catch {
      cleanup();
      setState('idle');
      setError('Mikrofonen gick inte att starta. Tillåt mikrofonen i inställningarna.');
    }
  };
  const stop = () => { if (rec.current?.state === 'recording') rec.current.stop(); };
  const cancel = () => { cancelled.current = true; stop(); };

  return { state, error, elapsed, start, stop, cancel, clearError: () => setError('') };
}
